import React from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Toaster } from "../ui/sonner";
import { Button } from "../ui/button";
import {
    LayoutDashboard,
    Building2,
    Settings,
    LogOut,
    Shield,
    CreditCard,
    Tag,
    FileText,
} from "lucide-react";
import { cn } from "../../lib/utils";

const adminNav = [
    { path: "/admin", label: "Vue d'ensemble", icon: LayoutDashboard, exact: true },
    { path: "/admin/tenants", label: "Entreprises", icon: Building2 },
    { path: "/admin/subscriptions", label: "Abonnements", icon: CreditCard },
    { path: "/admin/plans", label: "Plans & Tarifs", icon: Tag },
    { path: "/admin/audit", label: "Journal d'audit", icon: FileText },
    { path: "/admin/settings", label: "Configuration", icon: Settings },
];

export const SuperAdminLayout = () => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-background" data-testid="admin-layout">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-card border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-violet-600 flex items-center justify-center">
                                <Shield className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="font-heading font-bold text-base text-foreground leading-none">
                                    Orkestria
                                </h1>
                                <p className="text-xs text-violet-500 font-medium">Administration</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-muted-foreground hidden sm:block">
                                {user?.name || user?.email}
                            </span>
                            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
                                <LogOut className="w-4 h-4 mr-2" />
                                Déconnexion
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Nav */}
            <nav className="bg-card border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex gap-1 overflow-x-auto py-1">
                        {adminNav.map((item) => {
                            const isActive = item.exact
                                ? location.pathname === item.path
                                : location.pathname.startsWith(item.path);
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    data-testid={`admin-nav-${item.path.split("/").pop() || "dashboard"}`}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap",
                                        isActive
                                            ? "bg-violet-500/10 text-violet-600 dark:text-violet-400"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                    )}
                                >
                                    <item.icon className="w-4 h-4" />
                                    {item.label}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </nav>

            {/* Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Outlet />
            </main>

            <Toaster position="top-right" richColors />
        </div>
    );
};
